import { useState } from "react";

import { Button, Colors } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import participantsImage from "@/../../hypetrain/static/img/program/participants.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { LaunchProgramComponentDialog } from "@/components/pages_logged_in/program/components/LaunchProgramComponentDialog";
import { ProgramCard } from "@/components/pages_logged_in/program/components/ProgramCard";
import { ProgramLaunchingEmailSection } from "@/components/pages_logged_in/program/components/ProgramEmailSection";
import {
  ExpandBlock,
  isPanelDefaultVisible,
} from "@/components/pages_logged_in/program/components/utils";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { CARD_CLASSES, MIDTEXT_COLOR } from "@/css/constants";
import { ProgramStatus } from "@/graphql";
import { MainProgram } from "@/types/programs";
import { formatDate } from "@/utils/datetime";

type Dialogs = "launch-now" | "edit-launch-time";

interface LaunchProgramPanelProps {
  data: {
    icon: string;
    title: string;
    description: string;
  };
  program: MainProgram;
  expandBlock: ExpandBlock;
}

export function LaunchProgramPanel({
  data,
  program,
  expandBlock,
}: LaunchProgramPanelProps) {
  const navigate = useNavigate();
  const { icon, title, description } = data || {};
  const {
    id,
    scheduleLaunchEmail,
    targetLaunchDate,
    status,
    sendEnrollmentEmailAt,
    hasParticipants,
  } = program || {};

  const [dialogOpen, setDialogOpen] = useState<Dialogs | null>(null);

  const launchDateText = formatDate(
    targetLaunchDate,
    "eee. MMM d, yyyy 'at' hh:mm a z",
  );
  const canLaunch = status === ProgramStatus.LaunchReady;

  return (
    <Box
      flex={1}
      px={[16, 24]}
      py={[16, 30]}
      mb={24}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
      maxWidth={720}
    >
      <Flex pb={24} className="bbs" alignItems="center">
        <Flex alignItems={"center"} width={"100%"}>
          <EmbedSvg src={icon} height={50} width={50} />
          <Box mx={12} flex={1}>
            <Box fontSize={20} fontWeight={600}>
              {title}
            </Box>
            <Box fontSize={16} color={MIDTEXT_COLOR}>
              {description}
            </Box>
          </Box>
        </Flex>
      </Flex>

      <ProgramCard
        title="Decide Participants"
        description="Select the participants who will benefit most from your program. We will invite them on launch day."
        isCompleted={hasParticipants ?? false}
        image={participantsImage}
        buttonText={hasParticipants ? "See Participants" : "Set Participants"}
        onAddClick={() => {
          navigate(`/programs/${program?.id}/participants`);
        }}
        defaultVisible={isPanelDefaultVisible(program, "Participants")}
      />

      <ProgramCard
        title="Schedule Launch Emails"
        description=""
        isChildrenBelow={true}
        isCompleted={scheduleLaunchEmail}
        defaultVisible={expandBlock?.expandSubPanel === "ScheduleLaunchEmail"}
        onAddClick={() => {}}
      >
        <ProgramLaunchingEmailSection
          programId={id}
          emailSend={scheduleLaunchEmail}
          guideText={
            "We recommend sending at least two emails from you or an influential leader at your organization about the program before it launches. Please talk to your Exec contact about crafting an effective comms plan."
          }
        />
      </ProgramCard>
      <ProgramCard
        title="Automated Launch Email Goes Out"
        isCompleted={!!sendEnrollmentEmailAt}
        subTitle={
          <>
            Scheduled for{" "}
            <DateTimeDisplay
              time={targetLaunchDate}
              formatString="MMM d, yyyy"
            />
          </>
        }
        defaultVisible={expandBlock?.expandSubPanel === "AutomaticLaunchEmail"}
      >
        <Box mb={3}>
          Our system will automatically send an enrollment email to all of the
          participants on <b>{launchDateText}</b>
        </Box>
        <Box mb={3}>
          The subject of the email will be{" "}
          <b>Please read: You’re enrolled in {program?.name}</b> and it will
          come from hello@exec.com.
        </Box>
        {canLaunch && (
          <Box>
            <Button
              intent="primary"
              large
              onClick={() => {
                setDialogOpen("launch-now");
              }}
            >
              Launch Now
            </Button>

            <Button
              style={{ marginLeft: 10 }}
              intent="none"
              large
              onClick={() => {
                setDialogOpen("edit-launch-time");
              }}
            >
              Edit Schedule
            </Button>
          </Box>
        )}
      </ProgramCard>
      <LaunchProgramComponentDialog
        onClose={() => {
          setDialogOpen(null);
        }}
        program={program}
        open={dialogOpen === "launch-now" || dialogOpen === "edit-launch-time"}
        isUpdateNow={dialogOpen === "launch-now"}
      />
    </Box>
  );
}
